import React, { useEffect, useState } from "react";
import { Settings, Users2, ScrollText, Sparkles, Plug, Save, Trash2, UserPlus, Plus, KeyRound, Shield } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Field, inputCls } from "@/components/ui-kit";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";

const INTEGRATIONS = ["YouTube", "Spotify", "Google Drive", "Dropbox", "Planning Center", "ProPresenter", "Multitracks", "Ableton", "CCLI"];

export default function Admin() {
  const [settings, setSettings] = useState(null);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState({ builtin: [], custom: [], levels: [] });
  const [audit, setAudit] = useState([]);
  const [insights, setInsights] = useState(null);

  const loadRoles = () => api.get("/roles").then((r) => setRoles(r.data)).catch(() => {});
  const loadUsers = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data));
    loadUsers();
    loadRoles();
    api.get("/audit").then((r) => setAudit(r.data));
    api.get("/insights").then((r) => setInsights(r.data));
  }, []);

  const allRoles = [...roles.builtin, ...roles.custom];
  const roleLabel = (key) => allRoles.find((r) => r.key === key)?.label || key;

  const saveSettings = async () => {
    try { await api.put("/settings", settings); toast.success("Settings saved"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const changeRole = async (uid, role) => {
    try { await api.put(`/users/${uid}/role`, { role }); setUsers(users.map((u) => (u.id === uid ? { ...u, role } : u))); toast.success("Role updated"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const deleteUser = async (uid, name) => {
    if (!window.confirm(`Remove ${name}'s account? This can't be undone.`)) return;
    try { await api.delete(`/users/${uid}`); setUsers(users.filter((u) => u.id !== uid)); toast.success("User removed"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (!settings) return <div className="text-[#8A97AC]">Loading…</div>;

  return (
    <div>
      <PageHeader eyebrow="Administration" title="Church Settings" subtitle="Manage your workspace, people, and integrations." />
      <Tabs defaultValue="settings">
        <TabsList className="bg-[#0B101D] border border-white/10 mb-5 flex-wrap h-auto">
          <TabsTrigger value="settings" data-testid="admin-tab-settings"><Settings className="w-4 h-4 mr-1.5" /> General</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users"><Users2 className="w-4 h-4 mr-1.5" /> Users & Roles</TabsTrigger>
          <TabsTrigger value="integrations" data-testid="admin-tab-integrations"><Plug className="w-4 h-4 mr-1.5" /> Integrations</TabsTrigger>
          <TabsTrigger value="insights" data-testid="admin-tab-insights"><Sparkles className="w-4 h-4 mr-1.5" /> Insights</TabsTrigger>
          <TabsTrigger value="audit" data-testid="admin-tab-audit"><ScrollText className="w-4 h-4 mr-1.5" /> Audit Log</TabsTrigger>
        </TabsList>

        <TabsContent value="settings">
          <div className="wf-card rounded-2xl p-6 grid sm:grid-cols-2 gap-4 max-w-3xl">
            <Field label="Church name"><input className={inputCls} value={settings.church_name || ""} onChange={(e) => setSettings({ ...settings, church_name: e.target.value })} data-testid="setting-church-name" /></Field>
            <Field label="Timezone"><input className={inputCls} value={settings.timezone || ""} onChange={(e) => setSettings({ ...settings, timezone: e.target.value })} /></Field>
            <Field label="Default service time"><input className={inputCls} value={settings.default_service_time || ""} onChange={(e) => setSettings({ ...settings, default_service_time: e.target.value })} /></Field>
            <Field label="Default rehearsal day"><input className={inputCls} value={settings.default_rehearsal_day || ""} onChange={(e) => setSettings({ ...settings, default_rehearsal_day: e.target.value })} /></Field>
            <Field label="Currency"><input className={inputCls} value={settings.currency || ""} onChange={(e) => setSettings({ ...settings, currency: e.target.value })} /></Field>
            <Field label="CCLI number"><input className={inputCls} value={settings.ccli_number || ""} onChange={(e) => setSettings({ ...settings, ccli_number: e.target.value })} /></Field>
            <div className="sm:col-span-2">
              <button onClick={saveSettings} data-testid="save-settings" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl wf-gold-btn font-semibold text-sm"><Save className="w-4 h-4" /> Save settings</button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="users">
          <div className="max-w-3xl space-y-6">
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-display font-semibold text-[#F7F5F0]">People & access</h3>
                <AddUserDialog roles={allRoles} onDone={loadUsers} />
              </div>
              <div className="wf-card rounded-2xl overflow-hidden">
                {users.map((u) => (
                  <div key={u.id} className="flex items-center justify-between px-4 py-3 border-b border-white/5">
                    <div><div className="text-sm text-[#F7F5F0]">{u.name}</div><div className="text-xs text-[#6E7D97]">{u.email}</div></div>
                    <div className="flex items-center gap-2">
                      <select value={u.role} onChange={(e) => changeRole(u.id, e.target.value)} data-testid={`role-select-${u.id}`}
                        className="bg-[#0B101D] border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-[#F7F5F0]">
                        {roles.builtin.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                        {roles.custom.length > 0 && <optgroup label="Custom roles">{roles.custom.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}</optgroup>}
                      </select>
                      <button onClick={() => deleteUser(u.id, u.name)} data-testid={`delete-user-${u.id}`} title="Remove user"
                        className="p-1.5 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-300 hover:bg-rose-500/20"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                ))}
                {users.length === 0 && <div className="p-6 text-sm text-[#6E7D97]">Only admins can manage users.</div>}
              </div>
            </div>

            <CustomRoles roles={roles} onDone={loadRoles} />
          </div>
        </TabsContent>

        <TabsContent value="integrations">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl">
            {INTEGRATIONS.map((i) => (
              <div key={i} className="wf-card rounded-xl p-4 flex items-center justify-between">
                <div><div className="text-sm font-medium text-[#F7F5F0]">{i}</div><div className="text-xs text-[#8A97AC]">Not connected</div></div>
                <button className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs text-[#B8C1D1] hover:border-[#E5C378]/30">Configure</button>
              </div>
            ))}
          </div>
          <p className="text-xs text-[#6E7D97] mt-4 max-w-2xl">Integrations require configuration before they become active. Only upload and distribute material the church is legally permitted to use.</p>
        </TabsContent>

        <TabsContent value="insights">
          {insights && (
            <div className="grid sm:grid-cols-2 gap-4 max-w-3xl">
              <InsightCard title="Songs missing charts" items={insights.songs_no_charts} empty="Every song has a chart." />
              <InsightCard title="Songs missing copyright" items={insights.songs_no_copyright} empty="All copyright info present." />
              <div className="wf-card rounded-2xl p-5 sm:col-span-2">
                <h3 className="font-display font-semibold text-[#F7F5F0] mb-3">Key distribution</h3>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(insights.key_distribution).map(([k, v]) => (
                    <span key={k} className="px-3 py-1.5 rounded-lg bg-[#E5C378]/12 border border-[#E5C378]/30 text-sm font-mono text-[#E5C378]">{k}: {v}</span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="audit">
          <div className="wf-card rounded-2xl overflow-hidden max-w-3xl">
            {audit.map((a) => (
              <div key={a.id} className="px-4 py-3 border-b border-white/5 text-sm text-[#B8C1D1]">
                <span className="text-[#F7F5F0] font-medium">{a.actor}</span> {a.action} <span className="text-[#E5C378]">{a.target}</span>
                {a.detail && <span className="text-[#6E7D97]"> — {a.detail}</span>}
                <span className="block text-[11px] text-[#6E7D97] mt-0.5">{new Date(a.created_at).toLocaleString()}</span>
              </div>
            ))}
            {audit.length === 0 && <div className="p-6 text-sm text-[#6E7D97]">No activity recorded.</div>}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function AddUserDialog({ roles, onDone }) {
  const [open, setOpen] = useState(false);
  const genPwd = () => Math.random().toString(36).slice(2, 6) + Math.random().toString(36).slice(2, 6).toUpperCase();
  const [f, setF] = useState({ name: "", email: "", role: "worship_director", password: genPwd() });
  const [created, setCreated] = useState(null);

  const submit = async () => {
    if (!f.name.trim() || !f.email.trim()) { toast.error("Name and email are required"); return; }
    try {
      await api.post("/users", f);
      setCreated({ email: f.email, password: f.password });
      toast.success("Account created — share the temporary password");
      onDone && onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const reset = () => { setCreated(null); setF({ name: "", email: "", role: "worship_director", password: genPwd() }); };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <button data-testid="btn-add-user" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl wf-gold-btn font-semibold text-sm"><UserPlus className="w-4 h-4" /> Add user</button>
      </DialogTrigger>
      <DialogContent className="wf-glass border-white/10 max-w-md">
        <DialogHeader><DialogTitle className="font-display text-[#F7F5F0]">Add a user or administrator</DialogTitle></DialogHeader>
        {created ? (
          <div className="space-y-3">
            <p className="text-sm text-[#B8C1D1]">Account created. Share these credentials — they can change the password after signing in.</p>
            <div className="bg-[#0B101D] border border-white/10 rounded-xl p-3 space-y-1 text-sm">
              <div className="text-[#8A97AC]">Email <span className="text-[#F7F5F0] font-mono">{created.email}</span></div>
              <div className="text-[#8A97AC]">Temp password <span className="text-[#E5C378] font-mono" data-testid="new-user-password">{created.password}</span></div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => { navigator.clipboard.writeText(`Email: ${created.email}\nTemp password: ${created.password}`); toast.success("Copied"); }} className="flex-1 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-[#F7F5F0]">Copy</button>
              <button onClick={reset} data-testid="add-another-user" className="flex-1 py-2 rounded-xl wf-gold-btn font-semibold text-sm">Add another</button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <Field label="Full name"><input className={inputCls} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="new-user-name" /></Field>
            <Field label="Email"><input type="email" className={inputCls} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="new-user-email" /></Field>
            <Field label="Role">
              <select className={inputCls} value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} data-testid="new-user-role">
                {roles.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
              </select>
            </Field>
            <Field label="Temporary password">
              <div className="flex gap-2">
                <input className={inputCls} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="new-user-temp-password" />
                <button onClick={() => setF({ ...f, password: genPwd() })} title="Regenerate" className="px-3 rounded-xl bg-white/5 border border-white/10 text-[#B8C1D1]"><KeyRound className="w-4 h-4" /></button>
              </div>
            </Field>
            <button onClick={submit} data-testid="confirm-add-user" className="w-full py-2.5 rounded-xl wf-gold-btn font-semibold text-sm">Create account</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CustomRoles({ roles, onDone }) {
  const [label, setLabel] = useState("");
  const [level, setLevel] = useState("member");
  const levelLabel = (k) => roles.levels.find((l) => l.key === k)?.label || k;

  const create = async () => {
    if (!label.trim()) { toast.error("Enter a role name"); return; }
    try { await api.post("/roles", { label: label.trim(), level }); setLabel(""); setLevel("member"); toast.success("Role added"); onDone && onDone(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const remove = async (key, name) => {
    if (!window.confirm(`Delete the "${name}" role? Anyone with it becomes a Musician.`)) return;
    try { await api.delete(`/roles/${key}`); toast.success("Role deleted"); onDone && onDone(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <h3 className="font-display font-semibold text-[#F7F5F0] mb-3 flex items-center gap-2"><Shield className="w-4 h-4 text-[#E5C378]" /> Custom roles</h3>
      <div className="wf-card rounded-2xl p-5 space-y-4">
        <p className="text-xs text-[#8A97AC]">Create your own role names and choose what each can do. Permission levels: <span className="text-[#B8C1D1]">Admin</span> (full control), <span className="text-[#B8C1D1]">Content Manager</span> (plan services, songs, team), <span className="text-[#B8C1D1]">Member</span> (view + confirm), <span className="text-[#B8C1D1]">View Only</span>.</p>
        <div className="flex flex-wrap gap-2">
          {roles.custom.length === 0 && <span className="text-sm text-[#6E7D97]">No custom roles yet.</span>}
          {roles.custom.map((r) => (
            <span key={r.key} data-testid={`custom-role-${r.key}`} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#E5C378]/10 border border-[#E5C378]/30 text-sm text-[#F7F5F0]">
              {r.label} <span className="text-[10px] text-[#8A97AC] uppercase tracking-wide">{levelLabel(r.level)}</span>
              <button onClick={() => remove(r.key, r.label)} data-testid={`delete-role-${r.key}`} className="text-[#6E7D97] hover:text-rose-400"><Trash2 className="w-3.5 h-3.5" /></button>
            </span>
          ))}
        </div>
        <div className="grid sm:grid-cols-[1fr_auto_auto] gap-2 items-end">
          <Field label="Role name"><input className={inputCls} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Media Director" data-testid="new-role-label" /></Field>
          <Field label="Permission level">
            <select className={inputCls} value={level} onChange={(e) => setLevel(e.target.value)} data-testid="new-role-level">
              {roles.levels.map((l) => <option key={l.key} value={l.key}>{l.label}</option>)}
            </select>
          </Field>
          <button onClick={create} data-testid="confirm-add-role" className="h-[42px] px-4 rounded-xl wf-gold-btn font-semibold text-sm inline-flex items-center gap-1.5"><Plus className="w-4 h-4" /> Add role</button>
        </div>
      </div>
    </div>
  );
}

function InsightCard({ title, items, empty }) {
  return (
    <div className="wf-card rounded-2xl p-5">
      <h3 className="font-display font-semibold text-[#F7F5F0] mb-3">{title}</h3>
      {items.length === 0 ? <p className="text-sm text-emerald-300">{empty}</p> :
        <div className="flex flex-wrap gap-2">{items.map((s) => <span key={s} className="px-2.5 py-1 rounded-lg bg-white/5 border border-white/10 text-xs text-[#B8C1D1]">{s}</span>)}</div>}
    </div>
  );
}
